import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NearMeIcon from "@mui/icons-material/NearMe";
import EditIcon from "@mui/icons-material/Edit";
import GestureIcon from "@mui/icons-material/Gesture";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import LayerGroup from "ol/layer/Group";
import TileWMS from "ol/source/TileWMS";
import OSM from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import Draw from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import Snap from "ol/interaction/Snap";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { fromLonLat, toLonLat } from "ol/proj";
import "ol/ol.css";

import AppHeader from "./components/TopBar";
import AttributeForm from "./components/construction/AttributeForm";
import AwaitingApproval from "./components/construction/AwaitingApproval";
import HistoryList from "./components/construction/HistoryList";
import SketchList, { STATUS_COLOR } from "./components/construction/SketchList";
import {
  ActionDialog,
  NewSketchDialog,
} from "./components/construction/dialogs";
import { useAuth } from "./context/AuthContext";
import { GEOSERVER_CONFIG } from "./config/api";
import { createLayerGroups } from "./config/layerConfig";
import {
  approveSketch,
  createSketch,
  deleteSketch,
  exportSketch,
  getConfig,
  getSketch,
  listLayers,
  listSketches,
  rejectSketch,
  saveSketchFeatures,
  submitSketch,
} from "./services/constructionApi";
import { toggleGroupInGroups, toggleLayerInGroups } from "./utils/layerGroups";
import { APP_COLORS as C } from "./theme/colors";

const PANEL_WIDTH = 360;
const NOTICE_MS = 15000; // how long notices stay on screen
const PAGE_SIZE = 8; // sketches per page in the list
const PENDING_POLL_MS = 60000; // admin: re-check the approval queue every minute
const HISTORY_LIMIT = 100;
const SKETCH_GREEN = "#2e7d32";
const SELECT_CYAN = "#00acc1";

const GEO = { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" };
const GEO_WRITE = { ...GEO, decimals: 7 };
const geojson = new GeoJSON();

/** table name (= sketch layer_key) -> friendly name from the shared layer tree */
const LAYER_LABELS = (() => {
  const labels = {};
  Object.values(createLayerGroups()).forEach((group) =>
    Object.values(group.children).forEach((layer) => {
      if (layer.wmsLayer) labels[layer.wmsLayer.split(":")[1]] = layer.name;
    }),
  );
  return labels;
})();
const labelOf = (key) => LAYER_LABELS[key] || key;

function sketchStyle(feature, selected) {
  const type = feature.getGeometry()?.getType();
  if (type === "Point" || type === "MultiPoint") {
    return new Style({
      image: new CircleStyle({
        radius: selected ? 9 : 7,
        fill: new Fill({ color: SKETCH_GREEN }),
        stroke: new Stroke({ color: selected ? SELECT_CYAN : "#fff", width: 3 }),
      }),
    });
  }
  return new Style({
    stroke: new Stroke({
      color: selected ? SELECT_CYAN : SKETCH_GREEN,
      width: selected ? 6 : 4,
      lineDash: [10, 6],
    }),
  });
}

/**
 * Construction module — sketch tool.
 * Live layers come from GeoServer (same as Mapview). Edits are drawn into a
 * sketch that is saved to its own tables through the FastAPI backend; only an
 * admin's approval copies new features to the live layers.
 */
export default function Construction() {
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const userId = user?.user_id;

  // ---- map + interaction refs -------------------------------------------
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const coordRef = useRef(null);
  const wmsLayersRef = useRef({}); // layer key -> ol TileLayer
  const liveGroupRef = useRef(null);
  const sketchLayerRef = useRef(null);
  const sketchSourceRef = useRef(new VectorSource());
  const selectedRef = useRef(null);
  const toolRef = useRef("select");
  const activeLayerKeyRef = useRef("");
  const historyRef = useRef({ stack: [], idx: -1 });
  const savedSnapshotRef = useRef("[]");

  // ---- state -----------------------------------------------------------
  const [mapReady, setMapReady] = useState(false);
  const [layerGroups, setLayerGroups] = useState(createLayerGroups);
  const [expanded, setExpanded] = useState({});
  const [tab, setTab] = useState("layers");
  const [sketchLayers, setSketchLayers] = useState([]); // from /construction/layers
  const [sketches, setSketches] = useState([]);
  const [sketchPage, setSketchPage] = useState(1);
  const [sketchMeta, setSketchMeta] = useState({ pages: 0, total: 0 });
  const [pending, setPending] = useState([]); // submitted sketches (admin queue)
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingMeta, setPendingMeta] = useState({ pages: 0, total: 0 });
  const [pendingLoading, setPendingLoading] = useState(false);
  const [history, setHistory] = useState([]); // approved + rejected (admin)
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilter, setHistoryFilter] = useState(""); // "" | approved | rejected
  const [historyMeta, setHistoryMeta] = useState({ pages: 0, total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sketchesLoading, setSketchesLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [activeSketch, setActiveSketch] = useState(null);
  const [activeLayerKey, setActiveLayerKey] = useState("");
  const [tool, setTool] = useState("select");
  const [selected, setSelected] = useState(null); // ol Feature
  const [hist, setHist] = useState({ canUndo: false, canRedo: false });
  const [dirty, setDirty] = useState(false);
  const [featureCount, setFeatureCount] = useState(0);
  const [viewMode, setViewMode] = useState("both"); // both | live | sketch
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind: submit|approve|reject}
  const [hintVisible, setHintVisible] = useState(true);
  // Approve / Reject stay visible but disabled until the SERVER enables the review workflow
  const [reviewEnabled, setReviewEnabled] = useState(false);

  // the "open a sketch" hint stays for NOTICE_MS, then goes away
  useEffect(() => {
    const timer = setTimeout(() => setHintVisible(false), NOTICE_MS);
    return () => clearTimeout(timer);
  }, []);

  const notify = useCallback(
    (message, severity = "info") => setToast({ message, severity }),
    [],
  );

  const editableLayers = useMemo(
    () => sketchLayers.filter((l) => l.can_edit),
    [sketchLayers],
  );
  const editableKeys = useMemo(
    () => new Set(editableLayers.map((l) => l.layer_key)),
    [editableLayers],
  );
  const activeMeta = editableLayers.find((l) => l.layer_key === activeLayerKey);

  const isOwner = Boolean(activeSketch && activeSketch.created_by === userId);
  const canEditNow =
    isOwner && ["draft", "rejected"].includes(activeSketch?.status || "");

  activeLayerKeyRef.current = activeLayerKey;
  toolRef.current = tool;

  // ---- history (undo / redo) ------------------------------------------
  const snapshot = useCallback(
    () =>
      geojson.writeFeatures(sketchSourceRef.current.getFeatures(), GEO_WRITE),
    [],
  );

  const syncHistoryState = useCallback(() => {
    const h = historyRef.current;
    setHist({ canUndo: h.idx > 0, canRedo: h.idx < h.stack.length - 1 });
    setDirty(h.stack[h.idx] !== savedSnapshotRef.current);
    setFeatureCount(sketchSourceRef.current.getFeatures().length);
  }, []);

  const resetHistory = useCallback(() => {
    const s = snapshot();
    historyRef.current = { stack: [s], idx: 0 };
    savedSnapshotRef.current = s;
    syncHistoryState();
  }, [snapshot, syncHistoryState]);

  const pushHistory = useCallback(() => {
    const h = historyRef.current;
    h.stack = h.stack.slice(0, h.idx + 1);
    h.stack.push(snapshot());
    if (h.stack.length > HISTORY_LIMIT) h.stack.shift();
    h.idx = h.stack.length - 1;
    syncHistoryState();
  }, [snapshot, syncHistoryState]);

  const selectFeature = useCallback((feature) => {
    selectedRef.current = feature;
    setSelected(feature);
    sketchLayerRef.current?.changed();
  }, []);

  const restoreHistory = useCallback(
    (idx) => {
      const h = historyRef.current;
      if (idx < 0 || idx >= h.stack.length) return;
      h.idx = idx;
      const source = sketchSourceRef.current;
      source.clear();
      source.addFeatures(geojson.readFeatures(h.stack[idx], GEO));
      selectFeature(null);
      syncHistoryState();
    },
    [selectFeature, syncHistoryState],
  );

  const undo = () => restoreHistory(historyRef.current.idx - 1);
  const redo = () => restoreHistory(historyRef.current.idx + 1);

  /** Remove the selected feature from the sketch (Undo brings it back). */
  const deleteSelected = useCallback(() => {
    const feature = selectedRef.current;
    if (!feature || !canEditNow) return;
    sketchSourceRef.current.removeFeature(feature);
    selectFeature(null);
    pushHistory();
  }, [canEditNow, pushHistory, selectFeature]);

  // keyboard: Delete removes the selected feature, Ctrl+Z / Ctrl+Y undo / redo
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (
        ["INPUT", "TEXTAREA", "SELECT"].includes(t?.tagName) ||
        t?.isContentEditable ||
        !canEditNow
      ) {
        return;
      }
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (e.key === "Delete") {
        deleteSelected();
      } else if (ctrl && key === "z" && !e.shiftKey) {
        e.preventDefault();
        restoreHistory(historyRef.current.idx - 1);
      } else if (ctrl && (key === "y" || (key === "z" && e.shiftKey))) {
        e.preventDefault();
        restoreHistory(historyRef.current.idx + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEditNow, deleteSelected, restoreHistory]);

  // ---- map ------------------------------------------------------------
  useEffect(() => {
    const wms = {};
    const wmsOl = [];
    Object.values(layerGroups).forEach((group) => {
      Object.entries(group.children).forEach(([key, layer]) => {
        if (!layer.wmsLayer) return;
        const tile = new TileLayer({
          source: new TileWMS({
            url: layer.wmsUrl || GEOSERVER_CONFIG.wmsUrl,
            params: {
              LAYERS: layer.wmsLayer,
              FORMAT: "image/png8",
              TRANSPARENT: true,
              TILED: true,
            },
            serverType: "geoserver",
            crossOrigin: "anonymous",
          }),
          visible: group.visible && layer.visible,
        });
        wms[key] = tile;
        wmsOl.push(tile);
      });
    });
    wmsLayersRef.current = wms;
    liveGroupRef.current = new LayerGroup({ layers: wmsOl });

    sketchLayerRef.current = new VectorLayer({
      source: sketchSourceRef.current,
      zIndex: 1000,
      style: (feature) => sketchStyle(feature, feature === selectedRef.current),
    });

    const map = new Map({
      target: mapDivRef.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        liveGroupRef.current,
        sketchLayerRef.current,
      ],
      view: new View({
        center: fromLonLat([76.993869, 28.448841]),
        zoom: 10,
      }),
    });
    mapRef.current = map;

    map.on("singleclick", (evt) => {
      if (toolRef.current === "draw") return;
      const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
        layerFilter: (l) => l === sketchLayerRef.current,
        hitTolerance: 6,
      });
      selectFeature(feature || null);
    });
    map.on("pointermove", (evt) => {
      if (!coordRef.current || evt.dragging) return;
      const [lon, lat] = toLonLat(evt.coordinate);
      coordRef.current.textContent = `X: ${lon.toFixed(5)}   Y: ${lat.toFixed(5)}`;
    });

    setMapReady(true);
    return () => {
      setMapReady(false);
      map.setTarget(undefined);
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // live layer checkboxes -> OpenLayers
  useEffect(() => {
    Object.values(layerGroups).forEach((group) => {
      Object.entries(group.children).forEach(([key, layer]) => {
        wmsLayersRef.current[key]?.setVisible(group.visible && layer.visible);
      });
    });
  }, [layerGroups]);

  // compare mode: live only / sketch only / both
  useEffect(() => {
    liveGroupRef.current?.setVisible(viewMode !== "sketch");
    sketchLayerRef.current?.setVisible(viewMode !== "live");
  }, [viewMode, mapReady]);

  // ---- drawing interactions -------------------------------------------
  const onDrawEnd = useCallback(
    (evt) => {
      evt.feature.set("layer_key", activeLayerKeyRef.current);
      // drawend fires before the feature is in the source
      setTimeout(() => {
        pushHistory();
        selectFeature(evt.feature);
      }, 0);
    },
    [pushHistory, selectFeature],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return undefined;
    if (!canEditNow && tool !== "select") {
      setTool("select");
      return undefined;
    }
    const source = sketchSourceRef.current;
    const added = [];
    const add = (interaction) => {
      map.addInteraction(interaction);
      added.push(interaction);
    };
    if (canEditNow && tool === "modify") {
      const modify = new Modify({ source });
      modify.on("modifyend", pushHistory);
      add(modify);
    }
    if (canEditNow && tool === "draw" && activeMeta) {
      const draw = new Draw({ source, type: activeMeta.geometry_type });
      draw.on("drawend", onDrawEnd);
      add(draw);
    }
    if (added.length) add(new Snap({ source }));
    return () => added.forEach((i) => map.removeInteraction(i));
  }, [mapReady, tool, canEditNow, activeMeta, pushHistory, onDrawEnd]);

  // ---- data loading ---------------------------------------------------
  const refreshSketches = useCallback(
    async (status = statusFilter, page = sketchPage) => {
      setSketchesLoading(true);
      try {
        const query = (p) =>
          listSketches({ status: status || null, page: p, pageSize: PAGE_SIZE });
        let res = await query(page);
        if (res.pages > 0 && page > res.pages) {
          // the page emptied (e.g. after a delete): step back to the last one
          page = res.pages;
          res = await query(page);
        }
        setSketches(res.items);
        setSketchMeta({ pages: res.pages, total: res.total });
        setSketchPage(page);
      } catch (e) {
        notify(e.message, "error");
      } finally {
        setSketchesLoading(false);
      }
    },
    [statusFilter, sketchPage, notify],
  );

  const refreshPending = useCallback(
    async (page = pendingPage, silent = false) => {
      if (!isAdmin) return;
      if (!silent) setPendingLoading(true);
      try {
        const query = (p) =>
          listSketches({ status: "submitted", page: p, pageSize: PAGE_SIZE, scope: "all" });
        let res = await query(page);
        if (res.pages > 0 && page > res.pages) {
          page = res.pages;
          res = await query(page);
        }
        setPending(res.items);
        setPendingMeta({ pages: res.pages, total: res.total });
        setPendingPage(page);
      } catch (e) {
        if (!silent) notify(e.message, "error");
      } finally {
        setPendingLoading(false);
      }
    },
    [isAdmin, pendingPage, notify],
  );

  // admin: load the queue, and keep it fresh so new requests show up
  useEffect(() => {
    if (!isAdmin) return undefined;
    refreshPending(undefined, true);
    const timer = setInterval(() => refreshPending(undefined, true), PENDING_POLL_MS);
    return () => clearInterval(timer);
  }, [isAdmin, refreshPending]);

  const refreshHistory = useCallback(
    async (page = historyPage, filter = historyFilter, silent = false) => {
      if (!isAdmin) return;
      if (!silent) setHistoryLoading(true);
      try {
        const query = (p) =>
          listSketches({ status: filter || "reviewed", page: p, pageSize: PAGE_SIZE, scope: "all" });
        let res = await query(page);
        if (res.pages > 0 && page > res.pages) {
          page = res.pages;
          res = await query(page);
        }
        setHistory(res.items);
        setHistoryMeta({ pages: res.pages, total: res.total });
        setHistoryPage(page);
      } catch (e) {
        if (!silent) notify(e.message, "error");
      } finally {
        setHistoryLoading(false);
      }
    },
    [isAdmin, historyPage, historyFilter, notify],
  );

  useEffect(() => {
    getConfig()
      .then((cfg) => setReviewEnabled(Boolean(cfg.review_enabled)))
      .catch(() => setReviewEnabled(false));
    listLayers()
      .then((layers) => {
        setSketchLayers(layers);
        const first = layers.find((l) => l.can_edit);
        if (first) setActiveLayerKey((k) => k || first.layer_key);
      })
      .catch((e) => notify(e.message, "error"));
    refreshSketches("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // warn before closing the tab with unsaved edits
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const confirmDiscard = () =>
    !dirty ||
    window.confirm("You have unsaved changes in this sketch. Discard them?");

  const loadSketch = useCallback(
    async (id) => {
      const data = await getSketch(id);
      const source = sketchSourceRef.current;
      source.clear();
      source.addFeatures(geojson.readFeatures(data.features, GEO));
      setActiveSketch(data.sketch);
      selectFeature(null);
      setTool("select");
      resetHistory();
      const extent = source.getExtent();
      if (source.getFeatures().length && mapRef.current) {
        mapRef.current
          .getView()
          .fit(extent, { padding: [80, 80, 80, 80], maxZoom: 17, duration: 300 });
      }
      return data.sketch;
    },
    [resetHistory, selectFeature],
  );

  const run = useCallback(
    async (fn, okMessage) => {
      setBusy(true);
      try {
        const result = await fn();
        if (okMessage) notify(okMessage, "success");
        return result;
      } catch (e) {
        notify(e.message || "Something went wrong", "error");
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [notify],
  );

  const openSketch = async (id) => {
    if (id === activeSketch?.sketch_id) return;
    if (!confirmDiscard()) return;
    await run(() => loadSketch(id));
  };

  /** From the approval queue: make sure the sketch is open, then confirm approve/reject. */
  const reviewSketch = async (kind, sketch) => {
    if (sketch.sketch_id !== activeSketch?.sketch_id) {
      if (!confirmDiscard()) return;
      const ok = await run(async () => {
        await loadSketch(sketch.sketch_id);
        return true;
      });
      if (!ok) return;
    }
    setConfirm({ kind });
  };

  const openSketch2 = (sketch) => openSketch(sketch.sketch_id);

  const handleCreate = async (data) => {
    const created = await run(async () => {
      const sketch = await createSketch(data);
      await loadSketch(sketch.sketch_id);
      await refreshSketches(statusFilter, 1);
      return sketch;
    }, "Sketch created — pick a layer and start drawing");
    if (created) {
      setNewOpen(false);
      setTool("draw");
    }
  };

  // ---- save / submit / export / review --------------------------------
  const buildPayload = () =>
    sketchSourceRef.current.getFeatures().map((f) => {
      const { geometry, layer_key: layerKey, ...props } = f.getProperties();
      const clean = {};
      Object.entries(props).forEach(([k, v]) => {
        if (!k.startsWith("_")) clean[k] = v;
      });
      return {
        layer_key: layerKey,
        geometry: geojson.writeGeometryObject(f.getGeometry(), GEO_WRITE),
        properties: clean,
      };
    });

  const refreshActive = useCallback(async () => {
    const data = await getSketch(activeSketch.sketch_id);
    setActiveSketch(data.sketch);
    await refreshSketches();
    await refreshPending(undefined, true);
    await refreshHistory(undefined, undefined, true);
    return data.sketch;
  }, [activeSketch, refreshSketches, refreshPending, refreshHistory]);

  const saveNow = useCallback(async () => {
    await saveSketchFeatures(activeSketch.sketch_id, buildPayload());
    savedSnapshotRef.current = snapshot();
    syncHistoryState();
    await refreshActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSketch, snapshot, syncHistoryState, refreshActive]);

  const handleSave = () => run(saveNow, "Sketch saved");

  const handleExport = () =>
    run(async () => {
      if (canEditNow && dirty) await saveNow();
      return exportSketch(activeSketch.sketch_id);
    }, "Exported as GeoJSON");

  const clearActiveSketch = () => {
    sketchSourceRef.current.clear();
    setActiveSketch(null);
    selectFeature(null);
    setTool("select");
    resetHistory();
  };

  const handleConfirm = async (value) => {
    const { kind, sketch: target } = confirm;
    const done = await run(async () => {
      if (kind === "delete") {
        await deleteSketch(target.sketch_id);
        if (target.sketch_id === activeSketch?.sketch_id) clearActiveSketch();
        await refreshSketches();
        return true;
      }
      const id = activeSketch.sketch_id;
      if (kind === "submit") {
        if (dirty) await saveNow();
        await submitSketch(id);
      } else if (kind === "approve") {
        const res = await approveSketch(id);
        const total = Object.values(res.merged || {}).reduce((a, b) => a + b, 0);
        // live layers changed: bust the WMS tile cache
        Object.values(wmsLayersRef.current).forEach((l) =>
          l.getSource().updateParams({ _: Date.now() }),
        );
        notify(`Approved — ${total} feature(s) added to the live layers`, "success");
      } else if (kind === "reject") {
        await rejectSketch(id, value);
      }
      await refreshActive();
      return true;
    });
    if (done) {
      setConfirm(null);
      if (kind === "delete") notify(`Sketch “${target.name}” deleted`, "success");
      if (kind === "submit") notify("Submitted for approval", "success");
      if (kind === "reject") notify("Sketch rejected — the editor can fix and resubmit", "success");
    }
  };

  // ---- attribute form --------------------------------------------------
  const applyAttributes = (values) => {
    if (!selected) return;
    selected.setProperties(values);
    pushHistory();
    notify("Attributes updated (remember to Save)", "info");
  };

  const selectedMeta = selected
    ? sketchLayers.find((l) => l.layer_key === selected.get("layer_key"))
    : null;

  // ---- render helpers --------------------------------------------------
  const groupEntries = useMemo(() => Object.entries(layerGroups), [layerGroups]);
  const status = activeSketch?.status;
  const tooltipWhy = !activeSketch
    ? "Open or create a sketch first"
    : !isOwner
      ? "Only the owner can edit this sketch"
      : !canEditNow
        ? `A ${status} sketch cannot be edited`
        : "";

  const pillSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.25,
    px: 0.5,
    py: 0.25,
    bgcolor: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 2,
  };
  const toolBtnSx = {
    textTransform: "none",
    gap: 0.5,
    px: 1.25,
    py: 0.5,
    fontSize: 13,
    "&.Mui-selected": {
      bgcolor: C.primary,
      color: "#fff",
      "&:hover": { bgcolor: C.primaryDark },
    },
  };
  const modeBtnSx = {
    textTransform: "none",
    px: 1.25,
    py: 0.5,
    fontSize: 13,
    "&.Mui-selected": {
      bgcolor: C.primaryLight,
      color: C.primary,
      fontWeight: 700,
    },
  };

  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={tool}
        onChange={(_, v) => v && setTool(v)}
        sx={{ bgcolor: "#fff" }}
      >
        <ToggleButton value="select" sx={toolBtnSx}>
          <Tooltip title="Select a sketch feature">
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <NearMeIcon fontSize="small" /> Select
            </Box>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="draw" disabled={!canEditNow || !activeMeta} sx={toolBtnSx}>
          <Tooltip title={tooltipWhy || "Draw a new feature (double-click ends a line)"}>
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <GestureIcon fontSize="small" /> Draw
            </Box>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="modify" disabled={!canEditNow} sx={toolBtnSx}>
          <Tooltip title={tooltipWhy || "Modify: move a point, or move / add / remove a line's nodes"}>
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <EditIcon fontSize="small" /> Modify
            </Box>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={pillSx}>
        <Tooltip title="Undo (Ctrl+Z)">
          <span>
            <IconButton size="small" disabled={!hist.canUndo || !canEditNow} onClick={undo}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Y)">
          <span>
            <IconButton size="small" disabled={!hist.canRedo || !canEditNow} onClick={redo}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Tooltip title="Delete the selected feature (Delete key)">
          <span>
            <IconButton size="small" color="error" disabled={!selected || !canEditNow} onClick={deleteSelected}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Select
        size="small"
        value={activeMeta ? activeLayerKey : ""}
        displayEmpty
        disabled={!canEditNow || editableLayers.length === 0}
        onChange={(e) => setActiveLayerKey(e.target.value)}
        renderValue={(v) => (v ? labelOf(v) : "Layer to draw")}
        sx={{ minWidth: 180, fontSize: 13, bgcolor: "#fff" }}
      >
        {editableLayers.map((l) => (
          <MenuItem key={l.layer_key} value={l.layer_key}>
            {labelOf(l.layer_key)} ({l.geometry_type === "Point" ? "point" : "line"})
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  const actions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={viewMode}
        onChange={(_, v) => v && setViewMode(v)}
        sx={{ bgcolor: "#fff" }}
      >
        <ToggleButton value="live" sx={modeBtnSx}>Live only</ToggleButton>
        <ToggleButton value="sketch" sx={modeBtnSx}>Sketch only</ToggleButton>
        <ToggleButton value="both" sx={modeBtnSx}>Live + Sketch</ToggleButton>
      </ToggleButtonGroup>
      {activeSketch && (
        <Box sx={{ ...pillSx, gap: 0.75, px: 1, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, maxWidth: 180, color: C.text }} noWrap>
            {activeSketch.name}
          </Typography>
          <Chip size="small" label={status} color={STATUS_COLOR[status] || "default"} sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }} />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          {canEditNow && (
            <Button size="small" variant="outlined" startIcon={<SaveIcon />} disabled={busy || !dirty} onClick={handleSave}>
              Save
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={busy || (featureCount === 0 && !dirty)} onClick={handleExport}>
            Export
          </Button>
          {canEditNow && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<SendIcon />}
              disabled={busy || featureCount === 0}
              onClick={() => setConfirm({ kind: "submit" })}
            >
              Submit
            </Button>
          )}
          {isAdmin && (
            <>
              <Tooltip title={reviewEnabled ? "" : "Approval workflow — coming soon"}>
                <span>
                  <Button size="small" color="success" variant="contained" disableElevation startIcon={<CheckCircleIcon />} disabled={!reviewEnabled || busy || status !== "submitted"} onClick={() => setConfirm({ kind: "approve" })}>
                    Approve
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={reviewEnabled ? "" : "Approval workflow — coming soon"}>
                <span>
                  <Button size="small" color="error" variant="contained" disableElevation startIcon={<CancelIcon />} disabled={!reviewEnabled || busy || status !== "submitted"} onClick={() => setConfirm({ kind: "reject" })}>
                    Reject
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      )}
    </Box>
  );

  // toolbar + actions live in the navigation bar (the Home › Construction row)
  const headerBar = (
    <Box
      sx={{
        flex: 1,
        ml: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
        minWidth: 0,
      }}
    >
      {toolbar}
      {actions}
    </Box>
  );

  const confirmProps = {
    submit: {
      title: "Submit for approval?",
      message: "After submitting you can't edit this sketch unless the admin rejects it.",
      confirmLabel: "Submit",
    },
    approve: {
      title: "Approve this sketch?",
      message: `All ${activeSketch?.feature_count ?? ""} new feature(s) will be added to the LIVE layers and the dashboard will update. This can't be undone from here.`,
      confirmLabel: "Approve & merge",
      color: "success",
    },
    delete: {
      title: "Delete this sketch?",
      message: `“${confirm?.sketch?.name ?? ""}” and its ${confirm?.sketch?.feature_count ?? 0} feature(s) will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete",
      color: "error",
    },
    reject: {
      title: "Reject this sketch?",
      message: "Nothing is added to the live layers. The editor sees your remarks and can fix and resubmit.",
      confirmLabel: "Reject",
      color: "error",
      inputLabel: "Remarks for the editor (required)",
    },
  }[confirm?.kind];

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <AppHeader>{headerBar}</AppHeader>
      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* ---------------- left panel ---------------- */}
        <Box
          sx={{
            width: PANEL_WIDTH,
            flexShrink: 0,
            borderRight: `1px solid ${C.border}`,
            bgcolor: C.surface,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              if (v === "sketches") refreshSketches();
              if (v === "awaiting") refreshPending();
              if (v === "history") refreshHistory();
            }}
            variant="fullWidth"
            sx={{ borderBottom: `1px solid ${C.border}`, minHeight: 44, "& .MuiTab-root": { textTransform: "none", fontSize: 13, minWidth: 0 } }}
          >
            <Tab value="layers" label="Layers" sx={{ minHeight: 44 }} />
            <Tab value="sketches" label="My sketches" sx={{ minHeight: 44 }} />
            {isAdmin && (
              <Tab
                value="awaiting"
                sx={{ minHeight: 44 }}
                label={
                  <Badge
                    color="warning"
                    badgeContent={pendingMeta.total}
                    max={99}
                    sx={{ "& .MuiBadge-badge": { right: -16, top: 6 }, pr: 1.5 }}
                  >
                    Awaiting
                  </Badge>
                }
              />
            )}
            {isAdmin && <Tab value="history" label="History" sx={{ minHeight: 44 }} />}
          </Tabs>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: tab === "layers" ? "auto" : "hidden" }}>
            {tab === "layers" && (
              <List dense disablePadding>
                {groupEntries.map(([groupKey, group]) => (
                  <React.Fragment key={groupKey}>
                    <ListItemButton
                      onClick={() => setExpanded((p) => ({ ...p, [groupKey]: !p[groupKey] }))}
                    >
                      <Checkbox
                        size="small"
                        edge="start"
                        checked={group.visible}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => setLayerGroups((p) => toggleGroupInGroups(p, groupKey))}
                      />
                      <ListItemText
                        primary={group.name}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                      />
                      {expanded[groupKey] ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </ListItemButton>
                    <Collapse in={!!expanded[groupKey]} unmountOnExit>
                      {Object.entries(group.children).map(([key, layer]) => {
                        const table = layer.wmsLayer?.split(":")[1];
                        return (
                          <ListItemButton
                            key={key}
                            sx={{ pl: 5 }}
                            onClick={() => setLayerGroups((p) => toggleLayerInGroups(p, groupKey, key))}
                          >
                            <Checkbox size="small" edge="start" checked={layer.visible} tabIndex={-1} disableRipple />
                            <ListItemText primary={layer.name} primaryTypographyProps={{ fontSize: 13 }} />
                            {editableKeys.has(table) && (
                              <Chip size="small" color="success" variant="outlined" label="editable" sx={{ height: 18, fontSize: 10 }} />
                            )}
                          </ListItemButton>
                        );
                      })}
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            )}
            {tab === "awaiting" && isAdmin && (
              <AwaitingApproval
                items={pending}
                loading={pendingLoading}
                activeId={activeSketch?.sketch_id}
                page={pendingPage}
                pages={pendingMeta.pages}
                total={pendingMeta.total}
                pageSize={PAGE_SIZE}
                reviewEnabled={reviewEnabled}
                onView={openSketch2}
                onApprove={(sk) => reviewSketch("approve", sk)}
                onReject={(sk) => reviewSketch("reject", sk)}
                onPage={(p) => refreshPending(p)}
                onRefresh={() => refreshPending()}
              />
            )}
            {tab === "history" && isAdmin && (
              <HistoryList
                items={history}
                loading={historyLoading}
                activeId={activeSketch?.sketch_id}
                filter={historyFilter}
                page={historyPage}
                pages={historyMeta.pages}
                total={historyMeta.total}
                pageSize={PAGE_SIZE}
                onFilter={(v) => {
                  setHistoryFilter(v);
                  refreshHistory(1, v);
                }}
                onView={openSketch2}
                onPage={(p) => refreshHistory(p)}
                onRefresh={() => refreshHistory()}
              />
            )}
            {tab === "sketches" && (
              <SketchList
                sketches={sketches}
                loading={sketchesLoading}
                activeId={activeSketch?.sketch_id}
                userId={userId}
                statusFilter={statusFilter}
                page={sketchPage}
                pages={sketchMeta.pages}
                total={sketchMeta.total}
                pageSize={PAGE_SIZE}
                onFilter={(v) => {
                  setStatusFilter(v);
                  refreshSketches(v, 1);
                }}
                onPage={(p) => refreshSketches(statusFilter, p)}
                onOpen={openSketch}
                onNew={() => confirmDiscard() && setNewOpen(true)}
                onDelete={(sketch) => setConfirm({ kind: "delete", sketch })}
                onRefresh={() => refreshSketches()}
              />
            )}
          </Box>

        </Box>

        {/* ---------------- map ---------------- */}
        <Box sx={{ flex: 1, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, position: "relative", minHeight: 0 }}>
            <Box ref={mapDivRef} sx={{ position: "absolute", inset: 0 }} />
            {selected && (
              <AttributeForm
                key={selected.ol_uid}
                title={labelOf(selected.get("layer_key"))}
                geometryLabel={
                  selectedMeta
                    ? selectedMeta.geometry_type === "Point"
                      ? "Point"
                      : "Line"
                    : null
                }
                columns={selectedMeta?.columns || []}
                properties={selected.getProperties()}
                editable={canEditNow}
                onApply={applyAttributes}
                onDelete={deleteSelected}
                onClose={() => selectFeature(null)}
              />
            )}
            {!activeSketch && hintVisible && (
              <Alert
                severity="info"
                onClose={() => setHintVisible(false)}
                sx={{ position: "absolute", bottom: 40, left: 16, zIndex: 5 }}
              >
                Open a sketch from the “My sketches” tab, or create a new one, to start drawing.
              </Alert>
            )}
            <Paper elevation={3} sx={{ position: "absolute", bottom: 40, right: 12, p: 1, px: 1.5, zIndex: 5, fontSize: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 26, borderTop: `3px dashed ${SKETCH_GREEN}` }} /> Sketch (new)
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 26, borderTop: `3px dashed ${SELECT_CYAN}` }} /> Selected
              </Box>
            </Paper>
          </Box>
          <Box
            sx={{
              height: 28,
              px: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 3,
              borderTop: `1px solid ${C.border}`,
              bgcolor: C.surface,
              fontSize: 12,
              color: C.textMuted,
            }}
          >
            <span ref={coordRef}>X: -   Y: -</span>
            {activeSketch && (
              <span>
                Sketch: {status} · {featureCount} feature{featureCount === 1 ? "" : "s"}
                {dirty ? " · UNSAVED changes" : ""}
                {tool === "draw" ? " · click to add points, double-click to finish a line" : ""}
                {tool === "modify" ? " · Modify: drag a point or a node to move it, drag along a line to add a node, Alt+click a node to remove it" : ""}
              </span>
            )}
          </Box>
        </Box>
      </Box>

      <NewSketchDialog open={newOpen} busy={busy} onCreate={handleCreate} onClose={() => setNewOpen(false)} />
      {confirm && (
        <ActionDialog
          open
          busy={busy}
          {...confirmProps}
          onConfirm={handleConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={NOTICE_MS}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
